import { state } from '../state.js';
import { escapeHtml, formatRelativeTime } from '../utils/formatters.js';
import { showToast } from '../utils/toast.js';

export const CANONICAL_POLLING_TYPES = [
  { type: 'locales', label: 'Locales (locales)', stage: 1, stageName: 'System Config' },
  { type: 'currencies', label: 'Currencies (currencies)', stage: 1, stageName: 'System Config' },
  { type: 'channels', label: 'Channels (channels)', stage: 1, stageName: 'System Config' },
  { type: 'categories', label: 'Categories (categories)', stage: 4, stageName: 'Categories' },
  { type: 'attribute_groups', label: 'Attribute Groups (attribute_groups)', stage: 2, stageName: 'Attributes' },
  { type: 'attributes', label: 'Attributes (attributes)', stage: 2, stageName: 'Attributes' },
  { type: 'attribute_options', label: 'Attribute Options (attribute_options)', stage: 3, stageName: 'Options' },
  { type: 'families', label: 'Families (families)', stage: 5, stageName: 'Families' },
  { type: 'family_variants', label: 'Family Variants (family_variants)', stage: 5, stageName: 'Variants' },
  { type: 'asset_families', label: 'Asset Families (asset_families)', stage: 7, stageName: 'Asset Families' },
  { type: 'assets', label: 'Assets (assets)', stage: 7, stageName: 'Assets' },
  { type: 'product_models', label: 'Product Models (product_models)', stage: 8, stageName: 'Product Models' },
  { type: 'products', label: 'Products (products)', stage: 8, stageName: 'Products' },
];

/**
 * Fetch all continuous sync, polling, and webhook monitoring data from backend APIs
 */
export async function fetchMonitoringData(): Promise<void> {
  try {
    const [
      rtRes,
      pollRes,
      checkpointsRes,
      whStatsRes,
      whEventsRes,
      qStatsRes,
      qEventsRes,
      dlqRes,
    ] = await Promise.allSettled([
      fetch('/api/v1/sync/realtime/status'),
      fetch('/api/v1/sync/polling/status'),
      fetch('/api/v1/sync/polling/checkpoints'),
      fetch('/api/v1/webhooks/stats'),
      fetch('/api/v1/webhooks/events?limit=100'),
      fetch('/api/v1/webhooks/queue/stats'),
      fetch('/api/v1/webhooks/queue/events?limit=100'),
      fetch('/api/v1/webhooks/queue/dead-letter?limit=100'),
    ]);

    // 1. Real-time Sync Worker
    if (rtRes.status === 'fulfilled' && rtRes.value.ok) {
      const data = await rtRes.value.json();
      state.monitoring.realtime.isRunning = Boolean(data.isRunning);
      state.monitoring.realtime.isPaused = Boolean(data.isPaused);
      state.monitoring.realtime.inFlightCount = data.inFlightCount || 0;
      if (data.stats) {
        state.monitoring.realtime.stats = {
          totalProcessed: data.stats.totalProcessed || 0,
          totalSuccess: data.stats.totalSuccess || 0,
          totalFailed: data.stats.totalFailed || 0,
          totalRetried: data.stats.totalRetried || 0,
          lastEventAt: data.stats.lastEventAt || null,
        };
      }
    }

    // 2. Fallback Polling Worker
    if (pollRes.status === 'fulfilled' && pollRes.value.ok) {
      const data = await pollRes.value.json();
      state.monitoring.polling.isRunning = Boolean(data.isRunning);
      state.monitoring.polling.isPaused = Boolean(data.isPaused);
      state.monitoring.polling.isPolling = Boolean(data.isPolling);
      if (data.stats) {
        state.monitoring.polling.stats = {
          pollCount: data.stats.pollCount || 0,
          totalEntitiesPolled: data.stats.totalEntitiesPolled || 0,
          totalEntitiesSynced: data.stats.totalEntitiesSynced || 0,
          totalCreated: data.stats.totalCreated || 0,
          totalUpdated: data.stats.totalUpdated || 0,
          totalUnchanged: data.stats.totalUnchanged || 0,
          totalSkipped: data.stats.totalSkipped || 0,
          totalFailed: data.stats.totalFailed || 0,
          lastPollStartedAt: data.stats.lastPollStartedAt || null,
          lastPollCompletedAt: data.stats.lastPollCompletedAt || null,
          lastPollDurationMs: data.stats.lastPollDurationMs ?? null,
          nextPollScheduledAt: data.stats.nextPollScheduledAt || null,
        };
        if (data.stats.checkpoints) {
          state.monitoring.polling.checkpoints = data.stats.checkpoints;
        }
      }
    }

    // 2b. Checkpoints
    if (checkpointsRes.status === 'fulfilled' && checkpointsRes.value.ok) {
      const data = await checkpointsRes.value.json();
      if (data.checkpoints) {
        state.monitoring.polling.checkpoints = data.checkpoints;
      }
    }

    // 3. Webhook Listener Statistics
    if (whStatsRes.status === 'fulfilled' && whStatsRes.value.ok) {
      const data = await whStatsRes.value.json();
      if (data.stats) {
        state.monitoring.webhooks.stats = {
          totalRequests: data.stats.totalRequests || 0,
          successfulRequests: data.stats.successfulRequests || 0,
          failedRequests: data.stats.failedRequests || 0,
          totalEvents: data.stats.totalEvents || 0,
          lastRequestAt: data.stats.lastRequestAt || null,
          invalidSignatureCount: data.stats.invalidSignatureCount || 0,
          malformedCount: data.stats.malformedCount || 0,
          eventsByAction: data.stats.eventsByAction || {},
          eventsByEntityType: data.stats.eventsByEntityType || {},
        };
      }
    }

    // 3b. Webhook Recent Events Feed
    if (whEventsRes.status === 'fulfilled' && whEventsRes.value.ok) {
      const data = await whEventsRes.value.json();
      state.monitoring.webhooks.events = data.events || [];
    }

    // 4. Webhook Queue Statistics
    if (qStatsRes.status === 'fulfilled' && qStatsRes.value.ok) {
      const data = await qStatsRes.value.json();
      if (data.stats) {
        state.monitoring.queue.stats = {
          queueSize: data.stats.queueSize || 0,
          isPaused: Boolean(data.stats.isPaused),
          inFlightBatches: data.stats.inFlightBatches || 0,
          totalBatchesDispatched: data.stats.totalBatchesDispatched || 0,
          totalEventsEnqueued: data.stats.totalEventsEnqueued || 0,
          totalEventsProcessed: data.stats.totalEventsProcessed || 0,
          totalEventsFailed: data.stats.totalEventsFailed || 0,
          deadLetterQueueSize: data.stats.deadLetterQueueSize || 0,
          lastDispatchedAt: data.stats.lastDispatchedAt || null,
        };
      }
    }

    // 4b. Pending Queue Peek
    if (qEventsRes.status === 'fulfilled' && qEventsRes.value.ok) {
      const data = await qEventsRes.value.json();
      state.monitoring.queue.pendingEvents = data.events || [];
    }

    // 4c. Dead Letter Queue Items
    if (dlqRes.status === 'fulfilled' && dlqRes.value.ok) {
      const data = await dlqRes.value.json();
      state.monitoring.queue.deadLetterItems = data.items || [];
    }

    renderMonitoringOverview();
    renderWebhookEvents();
    renderPollingCheckpoints();
    renderQueueAndDlq();
  } catch (err) {
    console.warn('Failed to fetch continuous sync monitoring data:', err);
  }
}

/**
 * Render Top Monitoring Metric KPI Cards and Header Badges
 */
export function renderMonitoringOverview(): void {
  const { realtime, polling, webhooks, queue } = state.monitoring;

  // 1. Real-Time Worker Card
  const badgeRt = document.getElementById('badge-rt-worker-status');
  const metricRtStatus = document.getElementById('metric-rt-status');
  const metricRtInflight = document.getElementById('metric-rt-inflight');
  const metricRtProcessed = document.getElementById('metric-rt-processed');
  const metricRtSuccess = document.getElementById('metric-rt-success');
  const metricRtFailed = document.getElementById('metric-rt-failed');
  const metricRtLastEvent = document.getElementById('metric-rt-lastevent');
  const btnRtStart = document.getElementById('btn-rt-start') as HTMLButtonElement | null;
  const btnRtPause = document.getElementById('btn-rt-pause') as HTMLButtonElement | null;
  const btnRtStop = document.getElementById('btn-rt-stop') as HTMLButtonElement | null;

  if (badgeRt) {
    if (realtime.isRunning && !realtime.isPaused) {
      badgeRt.textContent = 'Running';
      badgeRt.className = 'badge badge-success';
    } else if (realtime.isPaused) {
      badgeRt.textContent = 'Paused';
      badgeRt.className = 'badge badge-warning';
    } else {
      badgeRt.textContent = 'Stopped';
      badgeRt.className = 'badge badge-neutral';
    }
  }

  if (metricRtStatus) {
    metricRtStatus.textContent = realtime.isRunning
      ? realtime.isPaused
        ? 'Paused'
        : 'Running (Active)'
      : 'Stopped (Idle)';
    metricRtStatus.className = `value mono ${
      realtime.isRunning && !realtime.isPaused ? 'text-success' : realtime.isPaused ? 'text-warning' : 'text-muted'
    }`;
  }

  if (metricRtInflight) metricRtInflight.textContent = `${realtime.inFlightCount} active`;
  if (metricRtProcessed) metricRtProcessed.textContent = `${realtime.stats.totalProcessed} entities`;
  if (metricRtSuccess) metricRtSuccess.textContent = String(realtime.stats.totalSuccess);
  if (metricRtFailed) metricRtFailed.textContent = String(realtime.stats.totalFailed);
  if (metricRtLastEvent) metricRtLastEvent.textContent = formatRelativeTime(realtime.stats.lastEventAt);

  if (btnRtStart) btnRtStart.disabled = realtime.isRunning && !realtime.isPaused;
  if (btnRtPause) {
    btnRtPause.disabled = !realtime.isRunning;
    btnRtPause.textContent = realtime.isPaused ? 'Resume' : 'Pause';
  }
  if (btnRtStop) btnRtStop.disabled = !realtime.isRunning;

  // 2. Fallback Polling Worker Card
  const badgePoll = document.getElementById('badge-poll-worker-status');
  const metricPollStatus = document.getElementById('metric-poll-status');
  const metricPollCycles = document.getElementById('metric-poll-cycles');
  const metricPollPolled = document.getElementById('metric-poll-polled');
  const metricPollSynced = document.getElementById('metric-poll-synced');
  const metricPollCreated = document.getElementById('metric-poll-created');
  const metricPollUpdated = document.getElementById('metric-poll-updated');
  const metricPollFailed = document.getElementById('metric-poll-failed');
  const metricPollLastRun = document.getElementById('metric-poll-lastrun');
  const btnPollStart = document.getElementById('btn-poll-start') as HTMLButtonElement | null;
  const btnPollPause = document.getElementById('btn-poll-pause') as HTMLButtonElement | null;
  const btnPollStop = document.getElementById('btn-poll-stop') as HTMLButtonElement | null;
  const pollStateDot = document.getElementById('poll-state-dot');
  const pollStateLabel = document.getElementById('poll-state-label');
  const pollNextScheduled = document.getElementById('poll-next-scheduled');

  if (badgePoll) {
    if (polling.isPolling) {
      badgePoll.textContent = 'Polling...';
      badgePoll.className = 'badge badge-accent';
    } else if (polling.isRunning && !polling.isPaused) {
      badgePoll.textContent = 'Running';
      badgePoll.className = 'badge badge-success';
    } else if (polling.isPaused) {
      badgePoll.textContent = 'Paused';
      badgePoll.className = 'badge badge-warning';
    } else {
      badgePoll.textContent = 'Stopped';
      badgePoll.className = 'badge badge-neutral';
    }
  }

  const pollStateText = polling.isPolling
    ? 'Polling in Progress'
    : polling.isRunning
    ? polling.isPaused
      ? 'Paused'
      : 'Running (Idle)'
    : 'Stopped';

  if (metricPollStatus) {
    metricPollStatus.textContent = pollStateText;
    metricPollStatus.className = `value mono ${
      polling.isPolling
        ? 'text-primary'
        : polling.isRunning && !polling.isPaused
        ? 'text-success'
        : polling.isPaused
        ? 'text-warning'
        : 'text-muted'
    }`;
  }

  if (pollStateDot) {
    pollStateDot.className = `pill-dot ${polling.isRunning && !polling.isPaused ? 'active' : ''}`;
  }
  if (pollStateLabel) pollStateLabel.textContent = pollStateText;

  if (metricPollCycles) metricPollCycles.textContent = `${polling.stats.pollCount} cycles`;
  if (metricPollPolled) metricPollPolled.textContent = String(polling.stats.totalEntitiesPolled);
  if (metricPollSynced) metricPollSynced.textContent = String(polling.stats.totalEntitiesSynced);
  if (metricPollCreated) metricPollCreated.textContent = `+${polling.stats.totalCreated} new`;
  if (metricPollUpdated) metricPollUpdated.textContent = `~${polling.stats.totalUpdated} upd`;
  if (metricPollFailed) metricPollFailed.textContent = `!${polling.stats.totalFailed} fail`;

  if (metricPollLastRun) {
    metricPollLastRun.textContent =
      polling.stats.lastPollCompletedAt && polling.stats.lastPollDurationMs !== null
        ? `${formatRelativeTime(polling.stats.lastPollCompletedAt)} (${polling.stats.lastPollDurationMs}ms)`
        : '--';
  }

  if (pollNextScheduled) {
    if (!polling.isRunning || polling.isPaused) {
      pollNextScheduled.textContent = 'Suspended';
    } else if (polling.stats.nextPollScheduledAt) {
      const remainingSec = Math.max(
        0,
        Math.round((new Date(polling.stats.nextPollScheduledAt).getTime() - Date.now()) / 1000)
      );
      pollNextScheduled.textContent = remainingSec > 0 ? `In ${remainingSec}s` : 'Imminent';
    } else {
      pollNextScheduled.textContent = 'Scheduled';
    }
  }

  if (btnPollStart) btnPollStart.disabled = polling.isRunning && !polling.isPaused;
  if (btnPollPause) {
    btnPollPause.disabled = !polling.isRunning;
    btnPollPause.textContent = polling.isPaused ? 'Resume' : 'Pause';
  }
  if (btnPollStop) btnPollStop.disabled = !polling.isRunning;

  // 3. Webhook Receiver Card
  const metricWhReceived = document.getElementById('metric-wh-received');
  const metricWhValid = document.getElementById('metric-wh-valid');
  const metricWhInvalid = document.getElementById('metric-wh-invalid');
  const metricWhMalformed = document.getElementById('metric-wh-malformed');
  const metricWhLastReceived = document.getElementById('metric-wh-lastreceived');

  if (metricWhReceived) metricWhReceived.textContent = `${webhooks.stats.totalRequests} received`;
  if (metricWhValid) metricWhValid.textContent = `${webhooks.stats.successfulRequests} verified`;
  if (metricWhInvalid) metricWhInvalid.textContent = `${webhooks.stats.invalidSignatureCount} invalid`;
  if (metricWhMalformed) metricWhMalformed.textContent = `${webhooks.stats.malformedCount} malf`;
  if (metricWhLastReceived) metricWhLastReceived.textContent = formatRelativeTime(webhooks.stats.lastRequestAt);

  // 4. Webhook Queue & DLQ Card
  const badgeQueue = document.getElementById('badge-wh-queue-status');
  const metricQueueBuffered = document.getElementById('metric-queue-buffered');
  const metricQueueInflight = document.getElementById('metric-queue-inflight');
  const metricQueueDispatched = document.getElementById('metric-queue-dispatched');
  const metricQueueDlq = document.getElementById('metric-queue-dlq');
  const metricQueueLastDispatched = document.getElementById('metric-queue-lastdispatched');
  const btnQueuePauseQuick = document.getElementById('btn-queue-pause-toggle-quick') as HTMLButtonElement | null;
  const btnQueuePauseMain = document.getElementById('btn-queue-pause-toggle') as HTMLButtonElement | null;

  if (badgeQueue) {
    if (queue.stats.isPaused) {
      badgeQueue.textContent = 'Paused';
      badgeQueue.className = 'badge badge-warning';
    } else if (queue.stats.queueSize > 0) {
      badgeQueue.textContent = `${queue.stats.queueSize} Buffered`;
      badgeQueue.className = 'badge badge-accent';
    } else {
      badgeQueue.textContent = 'Active';
      badgeQueue.className = 'badge badge-neutral';
    }
  }

  if (metricQueueBuffered) metricQueueBuffered.textContent = `${queue.stats.queueSize} pending`;
  if (metricQueueInflight) metricQueueInflight.textContent = `${queue.stats.inFlightBatches} in-flight`;
  if (metricQueueDispatched) metricQueueDispatched.textContent = `${queue.stats.totalBatchesDispatched} batches`;
  if (metricQueueDlq) {
    metricQueueDlq.textContent = `${queue.stats.deadLetterQueueSize} failed items`;
    metricQueueDlq.className = `value mono ${queue.stats.deadLetterQueueSize > 0 ? 'text-danger' : ''}`;
  }
  if (metricQueueLastDispatched) metricQueueLastDispatched.textContent = formatRelativeTime(queue.stats.lastDispatchedAt);

  const pauseText = queue.stats.isPaused ? 'Resume Queue' : 'Pause Queue';
  if (btnQueuePauseQuick) btnQueuePauseQuick.textContent = queue.stats.isPaused ? 'Resume' : 'Pause';
  if (btnQueuePauseMain) btnQueuePauseMain.textContent = pauseText;

  // Subtab count badges
  const countEvents = document.getElementById('subtab-events-count');
  const countCheckpoints = document.getElementById('subtab-checkpoints-count');
  const countDlq = document.getElementById('subtab-dlq-count');

  if (countEvents) countEvents.textContent = String(webhooks.events.length);
  if (countCheckpoints) countCheckpoints.textContent = String(Object.keys(polling.checkpoints).length || 13);
  if (countDlq) {
    countDlq.textContent = String(queue.deadLetterItems.length || queue.stats.deadLetterQueueSize || 0);
    countDlq.style.display = 'inline-block';
  }
}

/**
 * Render Live Webhook Events Table & Feed
 */
export function renderWebhookEvents(): void {
  const tbody = document.getElementById('wh-events-table-body');
  const emptyState = document.getElementById('wh-events-empty-state');
  const infoText = document.getElementById('wh-events-table-info');
  if (!tbody) return;

  const { events, filterType, filterAction, search } = state.monitoring.webhooks;

  let filtered = events;

  if (filterType) {
    filtered = filtered.filter(
      (e) => (e.entityType || '').toLowerCase() === filterType.toLowerCase()
    );
  }

  if (filterAction) {
    filtered = filtered.filter(
      (e) => (e.action || '').toLowerCase().includes(filterAction.toLowerCase()) ||
             (e.operation || '').toLowerCase() === filterAction.toLowerCase()
    );
  }

  if (search.trim()) {
    const q = search.toLowerCase().trim();
    filtered = filtered.filter(
      (e) =>
        (e.id && e.id.toLowerCase().includes(q)) ||
        (e.entityId && String(e.entityId).toLowerCase().includes(q)) ||
        (e.entityType && e.entityType.toLowerCase().includes(q)) ||
        (e.action && e.action.toLowerCase().includes(q)) ||
        (e.author && e.author.toLowerCase().includes(q))
    );
  }

  if (infoText) {
    infoText.textContent = `Showing ${filtered.length} of ${events.length} received webhook events`;
  }

  if (filtered.length === 0) {
    tbody.innerHTML = '';
    if (emptyState) emptyState.style.display = 'flex';
    return;
  }

  if (emptyState) emptyState.style.display = 'none';

  tbody.innerHTML = filtered
    .map((evt) => {
      const rawAction = evt.action || `${evt.entityType || 'entity'}.${evt.operation || 'updated'}`;
      const actionType = (evt.operation || (rawAction.includes('created') ? 'created' : rawAction.includes('deleted') ? 'deleted' : 'updated')).toLowerCase();
      const actionClass = actionType === 'created' ? 'action-created' : actionType === 'deleted' ? 'action-deleted' : 'action-updated';
      const entityType = evt.entityType || 'unknown';
      const entityId = evt.entityId || evt.id || '-';
      const sourceConn = evt.sourceConnectionId || 'default';
      const receivedTime = evt.receivedAt || evt.eventDate || evt.timestamp || new Date().toISOString();

      let sigBadge = `<span class="sig-badge-none" title="No signature header provided">Unsigned</span>`;
      if (evt.signatureValid === true || evt.verified === true) {
        sigBadge = `<span class="sig-badge-verified" title="HMAC SHA-256 Signature Verified">✓ Verified</span>`;
      } else if (evt.signatureValid === false) {
        sigBadge = `<span class="sig-badge-invalid" title="Signature Verification Failed">✕ Invalid</span>`;
      }

      return `
        <tr class="data-table-row" id="evt-row-${escapeHtml(evt.id)}">
          <td>
            <div class="job-id-cell">
              <span class="mono bold">${escapeHtml(evt.id)}</span>
              <span class="badge badge-neutral" style="font-size: 0.7rem;">${escapeHtml(sourceConn)}</span>
            </div>
          </td>
          <td>
            <span class="event-action-pill ${actionClass}">${escapeHtml(rawAction)}</span>
          </td>
          <td>
            <div class="entity-cell">
              <span class="entity-type-badge">${escapeHtml(entityType)}</span>
              <strong class="mono" style="color: var(--text-primary); margin-left: 0.35rem;">${escapeHtml(entityId)}</strong>
            </div>
          </td>
          <td>
            ${sigBadge}
          </td>
          <td>
            <div class="time-cell">
              <span class="time-relative">${escapeHtml(formatRelativeTime(receivedTime))}</span>
              <span class="time-exact mono">${new Date(receivedTime).toLocaleTimeString()}</span>
            </div>
          </td>
          <td style="text-align: right;">
            <div class="row-actions-group">
              <button class="btn btn-xs btn-secondary" onclick="window.AkeneoDashboard.openEventDetailModal('${escapeHtml(evt.id)}')">
                <span>Payload</span>
              </button>
              <button class="btn btn-xs btn-primary" onclick="window.AkeneoDashboard.syncEntityFromEvent('${escapeHtml(entityType)}', '${escapeHtml(entityId)}')">
                <span>Sync</span>
              </button>
            </div>
          </td>
        </tr>
      `;
    })
    .join('');
}

/**
 * Render Fallback Polling Watermark Checkpoints Table & Last Run Result Breakdown
 */
export function renderPollingCheckpoints(): void {
  const tbody = document.getElementById('checkpoints-table-body');
  if (!tbody) return;

  const storedCheckpoints = state.monitoring.polling.checkpoints || {};

  tbody.innerHTML = CANONICAL_POLLING_TYPES.map((item) => {
    const cpTimestamp = storedCheckpoints[item.type] || null;
    const cpFormatted = cpTimestamp ? new Date(cpTimestamp).toISOString() : 'None (Polls from default 1h)';
    const cpRelative = cpTimestamp ? formatRelativeTime(cpTimestamp) : 'Not initialized';
    const isSet = Boolean(cpTimestamp);

    return `
      <tr class="data-table-row" id="cp-row-${escapeHtml(item.type)}">
        <td>
          <div class="checkpoint-row-type">
            <span>📦</span>
            <span class="mono bold">${escapeHtml(item.type)}</span>
          </div>
        </td>
        <td>
          <span class="stage-badge">Stage ${item.stage} (${escapeHtml(item.stageName)})</span>
        </td>
        <td>
          <span class="mono ${isSet ? 'text-primary' : 'text-muted'}" style="font-size: 0.8125rem;">
            ${escapeHtml(cpFormatted)}
          </span>
        </td>
        <td>
          <span class="mono" style="font-size: 0.8125rem; color: var(--text-secondary);">
            ${escapeHtml(cpRelative)}
          </span>
        </td>
        <td style="text-align: right;">
          <div class="row-actions-group">
            <button class="btn btn-xs btn-secondary" onclick="window.AkeneoDashboard.openCheckpointEditModal('${escapeHtml(item.type)}')">
              <span>Edit</span>
            </button>
            <button class="btn btn-xs btn-secondary" onclick="window.AkeneoDashboard.saveCheckpoint('${escapeHtml(item.type)}', new Date().toISOString())" title="Set watermark to now">
              <span>Now</span>
            </button>
          </div>
        </td>
      </tr>
    `;
  }).join('');

  // Render Last Polling Cycle Details Breakdown
  renderLastPollResult();
}

/**
 * Render Last Polling Run Result breakdown
 */
export function renderLastPollResult(): void {
  const container = document.getElementById('last-poll-details-body');
  const badge = document.getElementById('last-poll-status-badge');
  const subtitle = document.getElementById('last-poll-summary-subtitle');
  if (!container) return;

  const { stats, lastResult } = state.monitoring.polling;

  if (!stats.lastPollStartedAt && !lastResult) {
    container.innerHTML = `
      <div class="empty-state-inline text-muted" style="padding: 1.5rem; text-align: center;">
        No fallback polling cycles executed yet in this session. Click "Trigger Immediate Poll" to run.
      </div>
    `;
    if (badge) {
      badge.textContent = 'Idle';
      badge.className = 'badge badge-neutral';
    }
    return;
  }

  if (badge) {
    badge.textContent = 'Completed';
    badge.className = 'badge badge-success';
  }

  if (subtitle && stats.lastPollCompletedAt) {
    subtitle.textContent = `Cycle completed at ${new Date(stats.lastPollCompletedAt).toLocaleTimeString()} (Duration: ${stats.lastPollDurationMs || 0}ms)`;
  }

  const resultsByEntityType = lastResult ? lastResult.resultsByEntityType || {} : {};
  const hasTypeBreakdown = Object.keys(resultsByEntityType).length > 0;

  let breakdownHtml = '';
  if (hasTypeBreakdown) {
    breakdownHtml = `
      <div class="modal-info-label" style="margin-top: 1rem; margin-bottom: 0.5rem;">Results by Entity Type:</div>
      <div class="last-poll-types-list">
        ${Object.entries(resultsByEntityType)
          .map(([type, res]: [string, any]) => `
            <div class="poll-type-row">
              <div class="poll-type-row-info">
                <span class="mono bold" style="color: var(--text-primary);">${escapeHtml(type)}</span>
                <span class="badge badge-neutral" style="font-size: 0.7rem;">${res.durationMs || 0}ms</span>
              </div>
              <div class="poll-type-counts">
                <span class="pill-mini pill-created">+${res.created || 0}</span>
                <span class="pill-mini pill-updated">~${res.updated || 0}</span>
                <span class="pill-mini pill-failed">!${res.failed || 0}</span>
                <span class="text-muted" style="font-size: 0.75rem;">(${res.entitiesPolled || 0} polled)</span>
              </div>
            </div>
          `)
          .join('')}
      </div>
    `;
  }

  container.innerHTML = `
    <div class="last-poll-stats-grid">
      <div class="last-poll-stat-item">
        <span class="poll-setting-label">Total Entities Polled</span>
        <span class="mono bold" style="font-size: 1.1rem; color: var(--text-primary);">${stats.totalEntitiesPolled}</span>
      </div>
      <div class="last-poll-stat-item">
        <span class="poll-setting-label">Total Entities Synced</span>
        <span class="mono bold text-success" style="font-size: 1.1rem;">${stats.totalEntitiesSynced}</span>
      </div>
      <div class="last-poll-stat-item">
        <span class="poll-setting-label">Execution Duration</span>
        <span class="mono" style="font-size: 1.1rem;">${stats.lastPollDurationMs || 0} ms</span>
      </div>
      <div class="last-poll-stat-item">
        <span class="poll-setting-label">Failed Errors</span>
        <span class="mono ${stats.totalFailed > 0 ? 'text-danger' : 'text-muted'}" style="font-size: 1.1rem;">${stats.totalFailed}</span>
      </div>
    </div>
    ${breakdownHtml}
  `;
}

/**
 * Render Queue Pending Buffer & Dead Letter Queue (DLQ)
 */
export function renderQueueAndDlq(): void {
  const pendingTbody = document.getElementById('queue-pending-table-body');
  const pendingEmpty = document.getElementById('queue-pending-empty-state');
  const dlqTbody = document.getElementById('dlq-table-body');
  const dlqEmpty = document.getElementById('dlq-empty-state');

  // 1. Pending Queue Buffer
  if (pendingTbody) {
    const pendingEvents = state.monitoring.queue.pendingEvents || [];
    if (pendingEvents.length === 0) {
      pendingTbody.innerHTML = '';
      if (pendingEmpty) pendingEmpty.style.display = 'flex';
    } else {
      if (pendingEmpty) pendingEmpty.style.display = 'none';
      pendingTbody.innerHTML = pendingEvents
        .map((evt) => `
          <tr class="data-table-row">
            <td><span class="mono bold">${escapeHtml(evt.id || 'evt_pending')}</span></td>
            <td><span class="event-action-pill action-updated">${escapeHtml(evt.action || 'updated')}</span></td>
            <td>
              <span class="entity-type-badge">${escapeHtml(evt.entityType || 'entity')}</span>
              <strong class="mono" style="margin-left: 0.35rem;">${escapeHtml(evt.entityId || '-')}</strong>
            </td>
            <td><span class="mono" style="font-size: 0.8rem;">${formatRelativeTime(evt.receivedAt || evt.timestamp)}</span></td>
            <td style="text-align: right;">
              <button class="btn btn-xs btn-secondary" onclick="window.AkeneoDashboard.flushWebhookQueue()">
                <span>Flush</span>
              </button>
            </td>
          </tr>
        `)
        .join('');
    }
  }

  // 2. Dead Letter Queue
  if (dlqTbody) {
    const dlqItems = state.monitoring.queue.deadLetterItems || [];
    if (dlqItems.length === 0) {
      dlqTbody.innerHTML = '';
      if (dlqEmpty) dlqEmpty.style.display = 'flex';
    } else {
      if (dlqEmpty) dlqEmpty.style.display = 'none';
      dlqTbody.innerHTML = dlqItems
        .map((item) => `
          <tr class="data-table-row">
            <td><span class="mono bold text-danger">${escapeHtml(item.id || 'dlq_item')}</span></td>
            <td>
              <span class="entity-type-badge">${escapeHtml(item.entityType || item.entity_type || 'entity')}</span>
              <strong class="mono" style="margin-left: 0.35rem;">${escapeHtml(item.entityId || item.entity_id || '-')}</strong>
            </td>
            <td>
              <div class="dlq-error-cell" title="${escapeHtml(item.errorMessage || item.error || 'Sync execution failed')}">
                ${escapeHtml(item.errorMessage || item.error || 'Sync execution failed')}
              </div>
            </td>
            <td><span class="mono">${item.retryCount || item.retries || 1}</span></td>
            <td><span class="mono" style="font-size: 0.8rem;">${formatRelativeTime(item.failedAt || item.timestamp)}</span></td>
            <td style="text-align: right;">
              <div class="row-actions-group">
                <button class="btn btn-xs btn-warning" onclick="window.AkeneoDashboard.retryDlqItem('${escapeHtml(item.id)}')">
                  <span>Retry</span>
                </button>
                <button class="btn btn-xs btn-outline-danger" onclick="window.AkeneoDashboard.clearDlq('${escapeHtml(item.id)}')">
                  <span>Delete</span>
                </button>
              </div>
            </td>
          </tr>
        `)
        .join('');
    }
  }
}

/**
 * Worker Controls: Real-Time Sync Worker
 */
export async function startRealtimeWorker(): Promise<void> {
  try {
    const res = await fetch('/api/v1/sync/realtime/start', { method: 'POST' });
    const data = await res.json();
    showToast(data.message || 'Real-time worker started', 'success');
    await fetchMonitoringData();
  } catch (err: any) {
    showToast(`Failed to start real-time worker: ${err.message}`, 'error');
  }
}

export async function stopRealtimeWorker(): Promise<void> {
  try {
    const res = await fetch('/api/v1/sync/realtime/stop', { method: 'POST' });
    const data = await res.json();
    showToast(data.message || 'Real-time worker stopped', 'info');
    await fetchMonitoringData();
  } catch (err: any) {
    showToast(`Failed to stop real-time worker: ${err.message}`, 'error');
  }
}

export async function pauseRealtimeWorker(): Promise<void> {
  const isPaused = state.monitoring.realtime.isPaused;
  const endpoint = isPaused ? '/api/v1/sync/realtime/resume' : '/api/v1/sync/realtime/pause';
  try {
    const res = await fetch(endpoint, { method: 'POST' });
    const data = await res.json();
    showToast(data.message || (isPaused ? 'Worker resumed' : 'Worker paused'), 'info');
    await fetchMonitoringData();
  } catch (err: any) {
    showToast(`Failed to pause/resume worker: ${err.message}`, 'error');
  }
}

export async function resetRealtimeWorkerStats(): Promise<void> {
  try {
    const res = await fetch('/api/v1/sync/realtime/reset-stats', { method: 'POST' });
    const data = await res.json();
    showToast(data.message || 'Worker statistics reset', 'success');
    await fetchMonitoringData();
  } catch (err: any) {
    showToast(`Failed to reset worker stats: ${err.message}`, 'error');
  }
}

/**
 * Worker Controls: Fallback Polling Worker
 */
export async function startPollingWorker(): Promise<void> {
  try {
    const res = await fetch('/api/v1/sync/polling/start', { method: 'POST' });
    const data = await res.json();
    showToast(data.message || 'Fallback polling worker started', 'success');
    await fetchMonitoringData();
  } catch (err: any) {
    showToast(`Failed to start polling worker: ${err.message}`, 'error');
  }
}

export async function stopPollingWorker(): Promise<void> {
  try {
    const res = await fetch('/api/v1/sync/polling/stop', { method: 'POST' });
    const data = await res.json();
    showToast(data.message || 'Fallback polling worker stopped', 'info');
    await fetchMonitoringData();
  } catch (err: any) {
    showToast(`Failed to stop polling worker: ${err.message}`, 'error');
  }
}

export async function pausePollingWorker(): Promise<void> {
  const isPaused = state.monitoring.polling.isPaused;
  const endpoint = isPaused ? '/api/v1/sync/polling/resume' : '/api/v1/sync/polling/pause';
  try {
    const res = await fetch(endpoint, { method: 'POST' });
    const data = await res.json();
    showToast(data.message || (isPaused ? 'Polling worker resumed' : 'Polling worker paused'), 'info');
    await fetchMonitoringData();
  } catch (err: any) {
    showToast(`Failed to pause/resume polling worker: ${err.message}`, 'error');
  }
}

/**
 * Trigger Immediate Fallback Polling Run
 */
export async function triggerPollingRun(options: Record<string, unknown> = {}): Promise<void> {
  try {
    showToast('Executing fallback polling run...', 'info', 3000);
    const res = await fetch('/api/v1/sync/polling/poll-now', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(options),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    state.monitoring.polling.lastResult = data.result || null;
    showToast(
      `Polling cycle completed: ${data.result?.totalEntitiesPolled || 0} polled, ${data.result?.totalEntitiesSynced || 0} synced`,
      'success',
      4000
    );
    await fetchMonitoringData();
  } catch (err: any) {
    showToast(`Polling run failed: ${err.message}`, 'error');
  }
}

export function openPollTriggerModal(): void {
  const modal = document.getElementById('poll-trigger-modal');
  if (modal) modal.style.display = 'flex';
}

export function closePollTriggerModal(): void {
  const modal = document.getElementById('poll-trigger-modal');
  if (modal) modal.style.display = 'none';
}

export async function confirmPollTrigger(): Promise<void> {
  const sinceInput = document.getElementById('poll-modal-since') as HTMLInputElement | null;
  const limitInput = document.getElementById('poll-modal-limit') as HTMLInputElement | null;
  const autoDepsInput = document.getElementById('poll-modal-auto-deps') as HTMLInputElement | null;
  const forceInput = document.getElementById('poll-modal-force') as HTMLInputElement | null;
  const dryRunInput = document.getElementById('poll-modal-dry-run') as HTMLInputElement | null;

  const options = {
    sinceTimestamp: sinceInput?.value.trim() || undefined,
    limitPerType: limitInput ? Number(limitInput.value) || 100 : 100,
    autoResolveDependencies: autoDepsInput ? autoDepsInput.checked : true,
    force: forceInput ? forceInput.checked : false,
    dryRun: dryRunInput ? dryRunInput.checked : false,
  };

  closePollTriggerModal();
  await triggerPollingRun(options);
}

/**
 * Checkpoint Management Handlers
 */
export function openCheckpointEditModal(entityType: string): void {
  const modal = document.getElementById('checkpoint-edit-modal');
  const typeInput = document.getElementById('edit-checkpoint-entity-type') as HTMLInputElement | null;
  const tsInput = document.getElementById('edit-checkpoint-timestamp') as HTMLInputElement | null;
  const titleType = document.getElementById('checkpoint-modal-type-name');

  if (typeInput) typeInput.value = entityType;
  if (titleType) titleType.textContent = `Entity: ${entityType}`;

  const currentVal = state.monitoring.polling.checkpoints[entityType] || new Date().toISOString();
  if (tsInput) tsInput.value = currentVal;

  if (modal) modal.style.display = 'flex';
}

export function closeCheckpointEditModal(): void {
  const modal = document.getElementById('checkpoint-edit-modal');
  if (modal) modal.style.display = 'none';
}

export async function saveCheckpoint(entityType: string, timestamp: string): Promise<void> {
  try {
    const res = await fetch('/api/v1/sync/polling/checkpoints', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ entity_type: entityType, timestamp }),
    });
    if (!res.ok) throw new Error('Failed to update checkpoint');
    showToast(`Checkpoint for ${entityType} saved`, 'success');
    await fetchMonitoringData();
  } catch (err: any) {
    showToast(`Error saving checkpoint: ${err.message}`, 'error');
  }
}

export async function saveCheckpointFromModal(): Promise<void> {
  const typeInput = document.getElementById('edit-checkpoint-entity-type') as HTMLInputElement | null;
  const tsInput = document.getElementById('edit-checkpoint-timestamp') as HTMLInputElement | null;
  const entityType = typeInput ? typeInput.value : '';
  const timestamp = tsInput ? tsInput.value.trim() : '';

  if (!entityType || !timestamp) {
    showToast('Valid timestamp is required', 'warning');
    return;
  }

  closeCheckpointEditModal();
  await saveCheckpoint(entityType, timestamp);
}

export async function setAllCheckpoints(offsetMs: number = 0): Promise<void> {
  const targetTs = new Date(Date.now() - offsetMs).toISOString();
  const checkpointsMap: Record<string, string> = {};
  for (const item of CANONICAL_POLLING_TYPES) {
    checkpointsMap[item.type] = targetTs;
  }

  try {
    const res = await fetch('/api/v1/sync/polling/checkpoints', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ checkpoints: checkpointsMap }),
    });
    if (!res.ok) throw new Error('Failed to update checkpoints');
    showToast(`All checkpoints set to ${offsetMs === 0 ? 'current time' : '1h ago'}`, 'success');
    await fetchMonitoringData();
  } catch (err: any) {
    showToast(`Error updating checkpoints: ${err.message}`, 'error');
  }
}

export async function resetAllCheckpoints(): Promise<void> {
  if (!confirm('Are you sure you want to reset all persisted watermark checkpoints?')) {
    return;
  }

  try {
    const res = await fetch('/api/v1/sync/polling/checkpoints', { method: 'DELETE' });
    if (!res.ok) throw new Error('Failed to reset checkpoints');
    showToast('All checkpoints reset successfully', 'info');
    await fetchMonitoringData();
  } catch (err: any) {
    showToast(`Error resetting checkpoints: ${err.message}`, 'error');
  }
}

/**
 * Webhook Queue & DLQ Controls
 */
export async function flushWebhookQueue(): Promise<void> {
  try {
    const res = await fetch('/api/v1/webhooks/queue/flush', { method: 'POST' });
    const data = await res.json();
    showToast(data.message || 'Queue buffer flushed successfully', 'success');
    await fetchMonitoringData();
  } catch (err: any) {
    showToast(`Error flushing queue: ${err.message}`, 'error');
  }
}

export async function toggleQueuePause(): Promise<void> {
  const isPaused = state.monitoring.queue.stats.isPaused;
  const endpoint = isPaused ? '/api/v1/webhooks/queue/resume' : '/api/v1/webhooks/queue/pause';
  try {
    const res = await fetch(endpoint, { method: 'POST' });
    const data = await res.json();
    showToast(data.message || (isPaused ? 'Queue resumed' : 'Queue paused'), 'info');
    await fetchMonitoringData();
  } catch (err: any) {
    showToast(`Error: ${err.message}`, 'error');
  }
}

export async function clearWebhookQueueBuffer(): Promise<void> {
  try {
    const res = await fetch('/api/v1/webhooks/queue', { method: 'DELETE' });
    const data = await res.json();
    showToast(data.message || 'Queue buffer cleared', 'info');
    await fetchMonitoringData();
  } catch (err: any) {
    showToast(`Error clearing queue: ${err.message}`, 'error');
  }
}

export async function retryDlqItem(id: string): Promise<void> {
  try {
    const res = await fetch('/api/v1/webhooks/queue/dead-letter/retry', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id }),
    });
    const data = await res.json();
    showToast(data.message || `Retried DLQ item ${id}`, 'success');
    await fetchMonitoringData();
  } catch (err: any) {
    showToast(`Error retrying DLQ item: ${err.message}`, 'error');
  }
}

export async function retryAllDlq(): Promise<void> {
  try {
    const res = await fetch('/api/v1/webhooks/queue/dead-letter/retry', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    });
    const data = await res.json();
    showToast(data.message || 'All dead-letter items retried', 'success');
    await fetchMonitoringData();
  } catch (err: any) {
    showToast(`Error retrying DLQ items: ${err.message}`, 'error');
  }
}

export async function clearDlq(id?: string): Promise<void> {
  try {
    const url = id ? `/api/v1/webhooks/queue/dead-letter?id=${encodeURIComponent(id)}` : '/api/v1/webhooks/queue/dead-letter';
    const res = await fetch(url, { method: 'DELETE' });
    const data = await res.json();
    showToast(data.message || 'Dead letter queue cleared', 'info');
    await fetchMonitoringData();
  } catch (err: any) {
    showToast(`Error clearing DLQ: ${err.message}`, 'error');
  }
}

export async function clearWebhookEventsFeed(): Promise<void> {
  try {
    const res = await fetch('/api/v1/webhooks/events', { method: 'DELETE' });
    const data = await res.json();
    showToast(data.message || 'Webhook event feed cleared', 'info');
    await fetchMonitoringData();
  } catch (err: any) {
    showToast(`Error clearing feed: ${err.message}`, 'error');
  }
}

export async function resetWebhookStats(): Promise<void> {
  try {
    const res = await fetch('/api/v1/webhooks/reset-stats', { method: 'POST' });
    const data = await res.json();
    showToast(data.message || 'Webhook statistics reset', 'success');
    await fetchMonitoringData();
  } catch (err: any) {
    showToast(`Error resetting stats: ${err.message}`, 'error');
  }
}

/**
 * Webhook Event Detail Modal
 */
export function openEventDetailModal(eventId: string): void {
  const event = state.monitoring.webhooks.events.find((e) => e.id === eventId);
  if (!event) {
    showToast('Event details not found', 'warning');
    return;
  }

  const modal = document.getElementById('event-detail-modal');
  const idText = document.getElementById('event-modal-id-text');
  const actionBadge = document.getElementById('event-modal-action-badge');
  const entityTypeEl = document.getElementById('event-modal-entity-type');
  const entityIdEl = document.getElementById('event-modal-entity-id');
  const sourceEl = document.getElementById('event-modal-source');
  const timestampEl = document.getElementById('event-modal-timestamp');
  const payloadEl = document.getElementById('event-modal-payload');
  const btnSyncNow = document.getElementById('btn-event-modal-sync-now') as HTMLButtonElement | null;

  if (idText) idText.textContent = `ID: ${event.id}`;
  if (actionBadge) actionBadge.textContent = event.action || 'event.updated';
  if (entityTypeEl) entityTypeEl.textContent = event.entityType || 'unknown';
  if (entityIdEl) entityIdEl.textContent = String(event.entityId || event.id);
  if (sourceEl) sourceEl.textContent = event.sourceConnectionId || 'default (Source)';
  if (timestampEl) timestampEl.textContent = event.receivedAt || event.eventDate || '--';
  if (payloadEl) payloadEl.textContent = JSON.stringify(event.payload || event, null, 2);

  if (btnSyncNow) {
    btnSyncNow.onclick = () => {
      closeEventDetailModal();
      syncEntityFromEvent(event.entityType, event.entityId);
    };
  }

  if (modal) modal.style.display = 'flex';
}

export function closeEventDetailModal(): void {
  const modal = document.getElementById('event-detail-modal');
  if (modal) modal.style.display = 'none';
}

/**
 * Sync Single Entity on-demand from Webhook Feed item
 */
export async function syncEntityFromEvent(entityType?: string, entityId?: string | number): Promise<void> {
  if (!entityType || !entityId) return;
  showToast(`Syncing ${entityType}: ${entityId}...`, 'info', 2000);
  try {
    const res = await fetch('/api/v1/sync/realtime/sync-entity', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        entity_type: entityType,
        entity_id: entityId,
        auto_resolve_dependencies: true,
      }),
    });
    const data = await res.json();
    if (data.success) {
      showToast(`Successfully synced ${entityType} "${entityId}" (${data.result?.action || 'synced'})`, 'success');
    } else {
      showToast(`Sync issue: ${data.message || 'Entity sync returned non-success'}`, 'warning');
    }
    await fetchMonitoringData();
  } catch (err: any) {
    showToast(`On-demand sync failed: ${err.message}`, 'error');
  }
}

/**
 * Simulate Inbound Webhook Payload for testing
 */
export async function simulateWebhookEvent(): Promise<void> {
  const sampleEntities = [
    { type: 'product', action: 'product.updated', id: `SKU_${Math.floor(1000 + Math.random() * 9000)}`, family: 'shoes' },
    { type: 'category', action: 'category.created', id: `cat_${Math.floor(100 + Math.random() * 900)}`, label: 'Summer Collection' },
    { type: 'product_model', action: 'product_model.updated', id: `MOD_${Math.floor(100 + Math.random() * 900)}`, family: 'apparel' },
    { type: 'attribute', action: 'attribute.created', id: `attr_${Math.floor(10 + Math.random() * 90)}`, attributeType: 'pim_catalog_text' },
  ];

  const pick = sampleEntities[Math.floor(Math.random() * sampleEntities.length)];
  const payload = {
    action: pick.action,
    author: 'api_test_user',
    event_date: new Date().toISOString(),
    data: {
      identifier: pick.id,
      code: pick.id,
      family: pick.family,
      label: pick.label,
    },
  };

  try {
    const res = await fetch('/api/v1/webhooks/akeneo', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    showToast(`Simulated webhook dispatched: ${pick.action} (${pick.id})`, 'success');
    await fetchMonitoringData();
  } catch (err: any) {
    showToast(`Simulation failed: ${err.message}`, 'error');
  }
}

/**
 * On-Demand Interactive Sync Tester Form
 */
export async function executeOnDemandSync(e?: Event): Promise<void> {
  if (e) e.preventDefault();

  const typeSelect = document.getElementById('ondemand-entity-type') as HTMLSelectElement | null;
  const idInput = document.getElementById('ondemand-entity-id') as HTMLInputElement | null;
  const autoDepsInput = document.getElementById('ondemand-auto-resolve') as HTMLInputElement | null;
  const forceInput = document.getElementById('ondemand-force') as HTMLInputElement | null;
  const dryRunInput = document.getElementById('ondemand-dry-run') as HTMLInputElement | null;
  const payloadText = document.getElementById('ondemand-payload') as HTMLTextAreaElement | null;
  const btnSubmit = document.getElementById('btn-run-ondemand-sync') as HTMLButtonElement | null;
  const btnText = document.getElementById('ondemand-btn-text');

  const entityType = typeSelect?.value;
  const entityId = idInput?.value.trim();

  if (!entityType || !entityId) {
    showToast('Entity Type and Identifier are required', 'warning');
    return;
  }

  let customPayload: unknown = undefined;
  if (payloadText && payloadText.value.trim()) {
    try {
      customPayload = JSON.parse(payloadText.value.trim());
    } catch {
      showToast('Invalid JSON in payload override textarea', 'error');
      return;
    }
  }

  if (btnSubmit) btnSubmit.disabled = true;
  if (btnText) btnText.textContent = 'Executing Sync...';

  try {
    const res = await fetch('/api/v1/sync/realtime/sync-entity', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        entity_type: entityType,
        entity_id: entityId,
        auto_resolve_dependencies: autoDepsInput ? autoDepsInput.checked : true,
        force: forceInput ? forceInput.checked : false,
        dry_run: dryRunInput ? dryRunInput.checked : false,
        payload: customPayload,
      }),
    });

    const data = await res.json();
    const resultPanel = document.getElementById('ondemand-result-panel');
    const resultIcon = document.getElementById('ondemand-result-icon');
    const resultTitle = document.getElementById('ondemand-result-title');
    const resultSubtitle = document.getElementById('ondemand-result-subtitle');
    const resultBadge = document.getElementById('ondemand-result-badge');
    const resultJson = document.getElementById('ondemand-result-json');
    const depsSection = document.getElementById('ondemand-dependencies-section');
    const depsPills = document.getElementById('ondemand-dependencies-pills');

    if (resultPanel) resultPanel.style.display = 'block';

    const isSuccess = Boolean(data.success);
    const syncResult = data.result || {};

    if (resultIcon) resultIcon.textContent = isSuccess ? '✅' : '❌';
    if (resultTitle) resultTitle.textContent = isSuccess ? `Sync Successful: ${entityId}` : `Sync Error: ${entityId}`;
    if (resultSubtitle) resultSubtitle.textContent = `Duration: ${syncResult.durationMs || 0}ms • Action: ${syncResult.action || 'failed'}`;

    if (resultBadge) {
      resultBadge.textContent = syncResult.action || (isSuccess ? 'success' : 'failed');
      resultBadge.className = `badge ${isSuccess ? 'badge-success' : 'badge-danger'}`;
    }

    if (resultJson) {
      resultJson.textContent = JSON.stringify(data, null, 2);
    }

    // Dependencies
    const resolvedDeps = syncResult.dependenciesResolved || [];
    if (depsSection && depsPills) {
      if (resolvedDeps.length > 0) {
        depsSection.style.display = 'block';
        depsPills.innerHTML = resolvedDeps
          .map(
            (dep: any) => `
            <div class="entity-pill">
              <span class="mono bold">${escapeHtml(dep.type)}</span>:
              <span class="mono">${escapeHtml(dep.id)}</span>
              <span class="pill-mini ${dep.action === 'created' ? 'pill-created' : 'pill-updated'}">${dep.action || 'resolved'}</span>
            </div>
          `
          )
          .join('');
      } else {
        depsSection.style.display = 'none';
      }
    }

    showToast(
      isSuccess ? `Synchronized ${entityType} "${entityId}"` : `Sync failed for ${entityType} "${entityId}"`,
      isSuccess ? 'success' : 'error'
    );
    await fetchMonitoringData();
  } catch (err: any) {
    showToast(`Sync execution error: ${err.message}`, 'error');
  } finally {
    if (btnSubmit) btnSubmit.disabled = false;
    if (btnText) btnText.textContent = 'Execute On-Demand Sync';
  }
}

/**
 * Monitoring Subtab Navigation
 */
export function switchMonitoringSubTab(subtabId: string): void {
  const validSubTabs = ['events', 'polling', 'queue', 'ondemand'];
  if (!validSubTabs.includes(subtabId)) subtabId = 'events';

  state.monitoring.activeSubTab = subtabId as any;

  document.querySelectorAll('#tab-monitoring .subtab-btn').forEach((btn) => {
    const isTarget = btn.getAttribute('data-subtab') === subtabId;
    btn.classList.toggle('active', isTarget);
    btn.setAttribute('aria-selected', isTarget ? 'true' : 'false');
  });

  const viewEvents = document.getElementById('monitoring-events-view');
  const viewPolling = document.getElementById('monitoring-polling-view');
  const viewQueue = document.getElementById('monitoring-queue-view');
  const viewOndemand = document.getElementById('monitoring-ondemand-view');

  if (viewEvents) viewEvents.style.display = subtabId === 'events' ? 'block' : 'none';
  if (viewPolling) viewPolling.style.display = subtabId === 'polling' ? 'block' : 'none';
  if (viewQueue) viewQueue.style.display = subtabId === 'queue' ? 'block' : 'none';
  if (viewOndemand) viewOndemand.style.display = subtabId === 'ondemand' ? 'block' : 'none';
}

/**
 * Live Monitoring Polling Timer
 */
export function startMonitoringLivePolling(): void {
  if (state.monitoring.liveTimer) clearInterval(state.monitoring.liveTimer);
  state.monitoring.isLivePolling = true;

  const dot = document.getElementById('monitoring-live-dot');
  const text = document.getElementById('monitoring-live-text');
  if (dot) dot.classList.add('active');
  if (text) text.textContent = 'Live: ON';

  state.monitoring.liveTimer = setInterval(() => {
    if (state.activeTab === 'monitoring' && state.monitoring.isLivePolling) {
      fetchMonitoringData();
    }
  }, 4000);
}

export function stopMonitoringLivePolling(): void {
  if (state.monitoring.liveTimer) {
    clearInterval(state.monitoring.liveTimer);
    state.monitoring.liveTimer = null;
  }
}

export function toggleMonitoringLivePolling(): void {
  state.monitoring.isLivePolling = !state.monitoring.isLivePolling;
  const dot = document.getElementById('monitoring-live-dot');
  const text = document.getElementById('monitoring-live-text');

  if (state.monitoring.isLivePolling) {
    if (dot) dot.classList.add('active');
    if (text) text.textContent = 'Live: ON';
    startMonitoringLivePolling();
    fetchMonitoringData();
    showToast('Live monitoring polling enabled', 'info', 1500);
  } else {
    if (dot) dot.classList.remove('active');
    if (text) text.textContent = 'Live: PAUSED';
    stopMonitoringLivePolling();
    showToast('Live monitoring polling paused', 'info', 1500);
  }
}
